import { CSSProperties, memo } from "react";
import classNames from "classnames";
import { LoyaltyRankProgress } from "components/atoms/LoyaltyRankProgress";
import LoyaltySkeleton from "components/atoms/LoyaltySkeleton/LoyaltySkeleton";
import LoyaltyFormulaExperienceContainer from "containers/atoms/LoyaltyFormulaExperienceContainer";
import PlaceholderRank from "assets/images/glory/placeholder_rank.webp";
import Arrow from "assets/icons/glory/arrow_small.svg?react";
import useTranslate from "hooks/useTranslate";
import "./styles.sass";

export type LoyaltyCardRankProps = {
  userExperience: number;
  rankExperience: number;
  prevRankExperience: number;
  nameRank: string;
  isRank?: boolean;
  image?: string;
  isActive?: boolean;
  handleClick?: () => void;
  styles?: CSSProperties;
  allRewardsIsGetting?: boolean;
  isFinal?: boolean;
  level?: string | number;
  isLoading?: boolean;
};

const LoyaltyCardRank = (props: LoyaltyCardRankProps) => {
  const {
    userExperience,
    rankExperience,
    prevRankExperience,
    nameRank,
    image = PlaceholderRank,
    isActive = false,
    handleClick,
    styles,
    isRank,
    allRewardsIsGetting,
    isFinal = false,
    level = undefined,
    isLoading,
  } = props;
  const t = useTranslate();
  const expBigger = userExperience >= rankExperience;
  const userAtCurrentLevel =
    userExperience >= prevRankExperience && userExperience < rankExperience;
  const experienceShowIsComplete = expBigger
    ? rankExperience
    : prevRankExperience;
  const experienceShow = userAtCurrentLevel
    ? userExperience
    : experienceShowIsComplete;
  const allRewardsIsGettingAndLevelComplete = allRewardsIsGetting && expBigger;

  const classNameImage = classNames(
    isRank ? "LoyaltyCardRank__imageRank" : "LoyaltyCardRank__image",
    isActive && !isRank && "LoyaltyCardRank__image_active",
    isActive && isRank && "LoyaltyCardRank__imageRank_active",
    isLoading && "LoyaltyCardRank__image_unvisible",
  );

  const classNameButton = classNames(
    "LoyaltyCardRank__button",
    expBigger && allRewardsIsGetting && "LoyaltyCardRank__button_active",
  );

  const classNameExp = classNames(
    "LoyaltyCardRank__experience",
    "LoyaltyCardRank__experience_complete",
  );

  const buttonLevelText =
    userExperience < prevRankExperience
      ? "loyalty.redesign.1.button.level.info.what-inside"
      : "loyalty.redesign.1.button.rewards";

  const buttonText = isRank
    ? "loyalty.redesign.1.button.rank.all-ranks"
    : buttonLevelText;

  return (
    <div className="LoyaltyCardRank" style={styles}>
      <LoyaltySkeleton active={isLoading} styles={{ borderRadius: "12px" }} />
      <img
        src={image}
        alt="image-rank"
        className={classNameImage}
        loading="lazy"
      />
      <div className="LoyaltyCardRank__content">
        <span className="LoyaltyCardRank__name">
          {t(nameRank)} {level || null}
        </span>
        {isRank ? (
          <span className="LoyaltyCardRank__text">
            {!isFinal && (
              <LoyaltyRankProgress
                isFinal={isFinal}
                userExperience={userExperience}
                rankExperience={rankExperience}
                prevRankExperience={prevRankExperience}
                className="LoyaltyCardRank__progressbar"
              />
            )}
            {t("loyalty.redesign.1.card.rank.description.rewards-to-come")}
          </span>
        ) : (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>
            {userAtCurrentLevel ? (
              <>
                <LoyaltyRankProgress
                  isFinal={isFinal}
                  userExperience={userExperience}
                  rankExperience={rankExperience}
                  prevRankExperience={prevRankExperience}
                  className="LoyaltyCardRank__progressbar"
                />
                <div className="LoyaltyCardRank__progress">
                  <div>
                    <span className="LoyaltyCardRank__experience">
                      <span className={classNameExp}>
                        {Math.round(experienceShow)}{" "}
                      </span>
                      {isFinal ? null : "/"} {rankExperience}
                    </span>
                  </div>
                  <LoyaltyFormulaExperienceContainer />
                </div>
              </>
            ) : (
              <div className="LoyaltyCardRank__levelDescription">
                {userExperience < prevRankExperience && (
                  <>
                    <LoyaltyRankProgress
                      isFinal={isFinal}
                      userExperience={userExperience}
                      rankExperience={rankExperience}
                      prevRankExperience={prevRankExperience}
                      className="LoyaltyCardRank__progressbar"
                    />
                    <span className="LoyaltyCardRank__levelDescriptionItem">
                      {t(
                        "loyalty.redesign.1.card.level.description.next-level",
                      )}
                    </span>
                  </>
                )}
                {expBigger && allRewardsIsGetting && (
                  <>
                    <span className="LoyaltyCardRank__levelDescriptionItem">
                      {t("loyalty.card.level.description.level-complete")}
                    </span>
                    <span className="LoyaltyCardRank__levelDescriptionItem">
                      {t("loyalty.card.level.description.rewards-getting")}
                    </span>
                  </>
                )}
                {expBigger && !allRewardsIsGetting && (
                  <>
                    <span className="LoyaltyCardRank__levelDescriptionItem">
                      {t("loyalty.redesign.1.card.level.description.complete")}
                    </span>
                    <span className="LoyaltyCardRank__levelDescriptionItem">
                      {t(
                        "loyalty.redesign.1.card.level.description.hurry-to-get-it",
                      )}
                    </span>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {!allRewardsIsGettingAndLevelComplete && (
          <button
            type="button"
            className={classNameButton}
            onClick={handleClick}
          >
            {t(buttonText)}
            <Arrow className="LoyaltyCardRank__buttonIcon" />
          </button>
        )}
      </div>
    </div>
  );
};

export default memo(LoyaltyCardRank);
